import { Icon, Stack, spacing, Text } from '@scality/core-ui';
import { Button } from '@scality/core-ui/dist/next';
import type React from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';
import { DashboardExportMenu } from '../export/DashboardExportMenu';
import type { ExportFormat } from '../export/renderToFile';
import type { DashboardProduct } from '../products';
import { type Control, type ControlOption, type ControlSelections, SCOPE_DIMENSIONS } from '../types/controls';
import { resolveControlComponent } from './controls/controlRegistry';
import { TimeRangeControl } from './controls/TimeRangeControl';

/** The dashboard's header: what you are looking at, above what it is scoped to. */
const Header = styled.div`
  display: flex;
  align-items: center;
  /* Last resort when even the shrunk criteria won't fit beside Apply. */
  flex-wrap: wrap;
  gap: ${spacing.r24};

  /* Outside the scroll area rather than sticky in it, so the scrollbar starts below. */
  flex: 0 0 auto;
  padding: ${spacing.r16} ${spacing.r16} ${spacing.r12};
  background-color: ${(props) => props.theme.backgroundLevel4};
  border-bottom: 1px solid ${(props) => props.theme.border};
`;

/** The scope sits above the title because it decides which deployment the title is about. */
const Selection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.r12};
  flex: 1;
  min-width: 12rem;
`;

/** Dashboard identity. */
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.r8};
`;

/** Every criterion on one row, period included — so Apply doesn't read as the period's button. */
const CriteriaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.r16};
  min-width: 0;
`;

/** The scope pickers, above the title. */
const ScopeRow = styled(CriteriaRow)``;

/** Centred against the whole selection block, not against whichever control it sits next to. */
const ApplyButton = styled(Button)`
  flex-shrink: 0;
  /* Fixed: the icon swaps to a narrower loader, and the button resized under the pointer. */
  min-width: 6rem;
  justify-content: center;
`;

type DashboardFiltersProps = {
  /** Dashboard name, shown as the header's title. */
  title: string;
  controls: Control[];
  templateProducts?: DashboardProduct[];
  options: Record<string, ControlOption[]>;
  /** What the backend applies for a control left empty. */
  defaultSelections?: ControlSelections;
  optionsLoading: boolean;
  staged: ControlSelections;
  stagedTimeRange: string | undefined;
  defaultTimeRange: string | undefined;
  isDirty: boolean;
  /** What must still be picked before there is anything valid to apply; absent once nothing is. */
  pendingPick?: string;
  /** Applying is in flight (the dashboard is re-rendering). */
  refreshing: boolean;
  exportingFormat: ExportFormat | null;
  /** False while the dashboard shows no widgets — Export stays in place, disabled. */
  canExport: boolean;
  onControlChange: (control: Control, values: string[]) => void;
  onTimeRangeChange: (value: string) => void;
  onRefresh: () => void;
  onExport: (format: ExportFormat) => void;
};

/**
 * The dashboard's pinned header: its name above, every criterion below, Apply beside both. A
 * control is disabled until every control it `dependsOn` has a value (cascade gating).
 *
 * Apply, not Refresh: it commits the criteria the bar shows and is disabled until something
 * changes, so it validates a selection rather than re-fetching what is already shown.
 */
export const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  title,
  controls,
  templateProducts,
  options,
  defaultSelections = {},
  optionsLoading,
  staged,
  stagedTimeRange,
  defaultTimeRange,
  isDirty,
  pendingPick,
  refreshing,
  exportingFormat,
  canExport,
  onControlChange,
  onTimeRangeChange,
  onRefresh,
  onExport,
}) => {
  const parentSelectionsByControlKey = useMemo(() => {
    const dimensionOfKey = new Map(controls.map((control) => [control.key, control.dimension]));
    return Object.fromEntries(
      controls.map((control) => [
        control.key,
        Object.fromEntries(
          (control.dependsOn ?? []).map((parentKey) => [
            dimensionOfKey.get(parentKey) ?? parentKey,
            staged[parentKey] ?? defaultSelections[parentKey] ?? [],
          ]),
        ),
      ]),
    );
  }, [controls, staged, defaultSelections]);

  // Anything whose dimension is not a scope one lands in the criteria row — the safe default.
  const { scopeControls, criteriaControls } = useMemo(
    () => ({
      scopeControls: controls.filter((control) => SCOPE_DIMENSIONS.includes(control.dimension)),
      criteriaControls: controls.filter((control) => !SCOPE_DIMENSIONS.includes(control.dimension)),
    }),
    [controls],
  );

  /** Answered means a staged value *or* a backend default — a ring on "Default" still scopes. */
  const answerOf = (controlKey: string) => staged[controlKey] ?? defaultSelections[controlKey] ?? [];

  const renderControl = (control: Control) => {
    const ControlComponent = resolveControlComponent(control);
    const parentUnset = (control.dependsOn ?? []).some((parent) => answerOf(parent).length === 0);
    return (
      <ControlComponent
        key={control.key}
        control={control}
        templateProducts={templateProducts}
        options={options[control.key] ?? []}
        value={staged[control.key] ?? []}
        defaultValue={defaultSelections[control.key]}
        parentSelections={parentSelectionsByControlKey[control.key]}
        loading={optionsLoading}
        disabled={parentUnset}
        onChange={(values) => onControlChange(control, values)}
      />
    );
  };

  return (
    <Header>
      <Selection>
        {scopeControls.length > 0 && <ScopeRow>{scopeControls.map(renderControl)}</ScopeRow>}
        <TitleRow>
          <Icon name="Dashboard" size="lg" color="textPrimary" />
          <Text variant="Larger" color="textPrimary">
            {title}
          </Text>
        </TitleRow>
        <CriteriaRow>
          {criteriaControls.map(renderControl)}
          <TimeRangeControl value={stagedTimeRange} defaultValue={defaultTimeRange} onChange={onTimeRangeChange} />
        </CriteriaRow>
      </Selection>
      <Stack gap="r8" style={{ flexShrink: 0 }}>
        <ApplyButton
          variant="primary"
          icon={<Icon name="Check" />}
          label="Apply"
          disabled={!isDirty || pendingPick !== undefined || refreshing}
          isLoading={refreshing}
          onClick={onRefresh}
          {...(pendingPick === undefined ? {} : { tooltip: { overlay: pendingPick } })}
        />
        <DashboardExportMenu exportingFormat={exportingFormat} disabled={!canExport} onExport={onExport} />
      </Stack>
    </Header>
  );
};
